import { Injectable } from '@angular/core';
import { difference, isEqual, sum } from 'lodash-es';
import { EMPTY, merge, Observable, of, timer } from 'rxjs';
import {
  auditTime,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  retry,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { TeamInfoService } from '@nimbus-library/features/shared/services/team/team-info.service';
import { ITeamContactInformation } from '@nimbus-global/features/shared/models/api/i-team-contact-information';
import { MediaLibraryApiClient } from '@nimbus-global/features/shared/services/api/media-library.api-client';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { NimbusSignalRHub } from '@nimbus-global/features/signalr/nimbusSignalR.hub';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class VoicemailsNewIndicatorService {
  private static readonly SIGNALR_AUDIT_MS = 500;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private static readonly DISCONNECTED_POLL_MS = 30 * 1000;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private static readonly CONNECTED_POLL_MS = 5 * 60 * 1000;
  private static readonly TEAM_INFO_RETRY_COUNT = 3;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private static readonly TEAM_INFO_RETRY_DELAY_MS = 5 * 1000;

  public readonly hasNewVoicemails$: Observable<boolean>;

  public constructor(
    private readonly permissionManagerService: IPermissionManagerService,
    private readonly teamInfoService: TeamInfoService,
    private readonly authenticationService: IAuthenticationService,
    private readonly mediaLibraryApiClient: MediaLibraryApiClient,
    private readonly signalRHub: NimbusSignalRHub,
  ) {
    this.hasNewVoicemails$ = this.getCountedTeamIds$().pipe(
      switchMap((teamIds) => (teamIds.length ? this.getNewCount$(teamIds) : of(0))),
      map((count) => count > 0),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.leaveGroupsOnPermissionLoss();
  }

  private getPermittedTeamIds$(): Observable<string[]> {
    return this.permissionManagerService.getTeamIds$(NimbusPermission.MedialibraryListCallRecords).pipe(
      map((teamIds) => teamIds ?? []),
      distinctUntilChanged(isEqual),
    );
  }

  // Runs for the service's lifetime, independent of whether the indicator is currently subscribed.
  private leaveGroupsOnPermissionLoss(): void {
    this.getPermittedTeamIds$()
      .pipe(startWith([] as string[]), pairwise(), untilDestroyed(this))
      .subscribe(([previousTeamIds, teamIds]) => this.leaveTeamGroups(difference(previousTeamIds, teamIds)));
  }

  private getCountedTeamIds$(): Observable<string[]> {
    return this.getPermittedTeamIds$().pipe(
      switchMap((teamIds) =>
        teamIds.length
          ? this.teamInfoService.getTeamInformation(teamIds).pipe(
              retry({
                count: VoicemailsNewIndicatorService.TEAM_INFO_RETRY_COUNT,
                delay: () => timer(VoicemailsNewIndicatorService.TEAM_INFO_RETRY_DELAY_MS),
              }),
              map((infos) => this.selectCountedTeamIds(teamIds, infos)),
              // Without team info, Direct Call Service ownership is unknown: count nothing rather than risk another user's DCS.
              catchError(() => of([])),
            )
          : of([]),
      ),
      distinctUntilChanged(isEqual),
    );
  }

  // A user with a personal Direct Call Service only gets the dot for that service.
  private selectCountedTeamIds(teamIds: string[], infos: IMap<ITeamContactInformation>): string[] {
    const userId = this.authenticationService.getUserInfo()?.userId;
    const personalDirectCallTeamId = teamIds.find(
      (teamId) => infos[teamId]?.isDirectCallService && infos[teamId].directCallOwningUserO365Id === userId,
    );
    if (personalDirectCallTeamId) {
      return [personalDirectCallTeamId];
    }
    return teamIds.filter((teamId) => infos[teamId] && !infos[teamId].isDirectCallService);
  }

  private getNewCount$(teamIds: string[]): Observable<number> {
    const normalizedTeamIds = new Set(teamIds.map((teamId) => teamId.toLowerCase()));

    // Groups are re-joined on every poll tick (and right after a reconnect) rather than left on teardown: the dashboard's
    // voicemail KPI providers share the same MedialibraryListCallRecords groups and leave them when destroyed.
    const poll$ = this.signalRHub.isConnected$.pipe(
      map(Boolean),
      distinctUntilChanged(),
      switchMap((isConnected) =>
        timer(0, isConnected ? VoicemailsNewIndicatorService.CONNECTED_POLL_MS : VoicemailsNewIndicatorService.DISCONNECTED_POLL_MS).pipe(
          tap(() => {
            if (isConnected) {
              this.joinTeamGroups(teamIds);
            }
          }),
        ),
      ),
    );
    const callRecordEvents$ = merge(this.signalRHub.onCallRecordCreated$, this.signalRHub.onCallRecordChanged$).pipe(
      filter((notification) => normalizedTeamIds.has(notification?.teamId?.toLowerCase())),
      auditTime(VoicemailsNewIndicatorService.SIGNALR_AUDIT_MS),
    );

    return merge(poll$, callRecordEvents$).pipe(
      switchMap(() =>
        this.mediaLibraryApiClient.getCallRecordCounts(teamIds, false, false).pipe(
          map((counts) => sum(Object.values(counts).map((response) => response?.count ?? response?.Count ?? 0))),
          catchError(() => EMPTY),
        ),
      ),
    );
  }

  private joinTeamGroups(teamIds: string[]): void {
    this.signalRHub
      .addToGroupsWithTeamId(teamIds, NimbusPermission.MedialibraryListCallRecords)
      ?.pipe(
        catchError(() => EMPTY),
        untilDestroyed(this),
      )
      .subscribe();
  }

  // Only groups of services the user lost access to are left, so consumers sharing the remaining groups are unaffected.
  private leaveTeamGroups(teamIds: string[]): void {
    if (!teamIds.length || !this.signalRHub.isConnected$.getValue()) {
      return;
    }
    this.signalRHub
      .removeFromGroupsWithTeamId(teamIds, NimbusPermission.MedialibraryListCallRecords)
      ?.pipe(
        catchError(() => EMPTY),
        untilDestroyed(this),
      )
      .subscribe();
  }
}
